import React from "react";
import { dbDateFormat } from "../../classes/utils/DateUtils";
import { mapOrientationOptionsDxToMorphology } from "../../classes/enums/MedicalImagesEnum";
import { useDateFormatter } from "../../hooks/useDateFormatter";
import Tooltip from "../tooltip/Tooltip";
import "./PosturalImageThumbnail.css";
import { useKeyboardActivation } from "../../hooks/useKeyboardActivation";
import useTranslation from "../../hooks/i18n/useTranslation";

export const PosturalImageThumbnail = ({ image, isSelected, onClick }) => {
  const { t } = useTranslation();
  const formatDate = useDateFormatter();
  // Get file name (title), remove extension, and clamp to first 5 letters
  const fullFileName = image?.title || t("346");
  const fileNameWithoutExtension = fullFileName.replace(/\.[^/.]+$/, ""); // Remove file extension
  const fileName = fileNameWithoutExtension.substring(0, 5) + "...";

  // Parse filename to extract viewType and map to position using enum
  const parts = fullFileName.split("_");
  const viewType = parts[2] || ""; // e.g., "SagittalPA", "CoronalLR"
  const viewTypeNormalized = viewType.replace(/\s+/g, "");

  // Use the enum mapping to get the correct position
  const mappedPosition = mapOrientationOptionsDxToMorphology[viewTypeNormalized] || "";

  const getOrientationAbbreviation = (position) => {
    if (!position) return "";
    const lower = position.toLowerCase();
    if (lower.includes("front")) return "F";
    if (lower.includes("back")) return "B";
    if (lower.includes("left")) return "L";
    if (lower.includes("right")) return "R";
    // Fallback: just take first letter uppercase
    return position.charAt(0).toUpperCase();
  };

  const orientationAbbrev = getOrientationAbbreviation(mappedPosition);

  // Helper to normalize date to YYYYMMDD format before formatting
  const normalizeDate = (dateValue) => {
    if (!dateValue) return null;

    // If already in YYYYMMDD format (8 digits), use as-is
    if (typeof dateValue === "string" && /^\d{8}$/.test(dateValue)) {
      return dateValue;
    }

    // If it's an ISO string or Date object, convert to YYYYMMDD
    return dbDateFormat(dateValue);
  };

  // Format the date if available
  const dateValue = image?.date || image?.acquisitionDate || image?.uploadDate;
  const normalizedDate = normalizeDate(dateValue);
  const formattedDate = normalizedDate
    ? formatDate(normalizedDate)
    : t("347");

  return (
    <div
      className={`postural-image-thumbnail ${isSelected ? "selected" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={useKeyboardActivation(onClick)}
    >
      <div className="postural-image-thumbnail-preview">
        <img
          src={image.url}
          alt={fileName}
          className="postural-image-thumbnail-image"
        />
        <div className="postural-image-thumbnail-overlay">
          <Tooltip text={fileNameWithoutExtension}>
            <div className="postural-image-thumbnail-overlay-name">
              {fileName}({orientationAbbrev})
            </div>
          </Tooltip>
          <div className="postural-image-thumbnail-overlay-date">
            {formattedDate}
          </div>
        </div>
      </div>

      <div className="postural-image-thumbnail-info">
        <Tooltip text={fileNameWithoutExtension}>
          <div className="postural-image-thumbnail-orientation">
            {fileName}
          </div>
        </Tooltip>
        <div className="postural-image-thumbnail-date">
          {formattedDate}
        </div>
      </div>

      {isSelected && <div className="postural-image-thumbnail-marker">&#10003;</div>}
    </div>
  );
};
